import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Sidebar } from "./Sidebar";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronLeft, ChevronRight, Settings, LogOut, Home } from "lucide-react";
import { Button } from "./ui/button";
import { usePageTitle } from "../utils/usePageTitle";
import { NotificationBell } from "./NotificationBell";
import { cn } from "./ui/utils";
import { isMobileHost } from "../utils/mobileHost";

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  // Update browser tab title based on current route
  usePageTitle();

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Detect if any modal or dialog is open to hide the mobile header
  useEffect(() => {
    const checkModals = () => {
      // Check for elements with role="dialog" or "alertdialog" 
      // This covers Radix UI Dialogs, Alert Dialogs, and Sheets
      const modals = document.querySelectorAll('[role="dialog"], [role="alertdialog"]');
      setIsAnyModalOpen(modals.length > 0);
    };

    // Initial check
    checkModals();

    // Observe body for changes to catch modals being added/removed from the DOM
    const observer = new MutationObserver(() => {
      checkModals();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading &&
      !currentUser &&
      !location.pathname.includes('/login') &&
      !location.pathname.includes('/signup') &&
      !location.pathname.includes('/verify-code') &&
      !location.pathname.includes('/reset-password') &&
      !location.pathname.includes('/account-setup') &&
      !location.pathname.includes('/offer-view')) {
      navigate('/login', { state: { from: location } });
    }
  }, [currentUser, loading, navigate, location]);

  // Helper to get display text for branding
  const getIdarahText = () => {
    if (window.location.hostname !== 'localhost') return "idarah";
    switch (currentUser?.userRole) {
      case "Admin": return "idarah-a";
      case "Director": return "idarah-d";
      case "Manager": return "idarah-m";
      case "Worker": return "idarah-w";
      default: return "idarah";
    }
  };

  // m.idarah.io runs a sidebar-free shell: always-visible top bar with a
  // home button back to the square-apps grid, no hamburger or drawer.
  const mobileHost = isMobileHost();
  const isMobileHome = location.pathname === "/home";

  // Don't show sidebar for auth pages
  if (location.pathname.includes('/login') ||
    location.pathname.includes('/signup') ||
    location.pathname.includes('/changepassword') ||
    location.pathname.includes('/verify-code') ||
    location.pathname.includes('/reset-password') ||
    location.pathname.includes('/account-setup') ||
    location.pathname.includes('/offer-view')) {
    return <div className="h-dvh overflow-y-auto">{children}</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row h-dvh overflow-hidden bg-background">

      {/* ─── Mobile Top Header Bar (Facebook-style) ─── */}
      <header className={cn(
        "fixed top-0 left-0 right-0 z-[60] h-14 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-3 shadow-sm transition-all duration-200",
        mobileHost ? "" : "lg:hidden",
        isAnyModalOpen ? "opacity-0 pointer-events-none -translate-y-full" : "opacity-100 translate-y-0"
      )}>
        {/* Left: Hamburger (desktop host) or Home (m.idarah.io) + Branding */}
        <div className="flex items-center gap-2">
          {mobileHost ? (
            !isMobileHome && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg text-gray-700 dark:text-gray-300"
                onClick={() => navigate("/home")}
                title="Home"
              >
                <Home size={20} />
              </Button>
            )
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-lg text-gray-700 dark:text-gray-300"
              onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            >
              {isMobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          )}
          <span className="text-lg font-bold tracking-tight text-gray-900 dark:text-white">
            {getIdarahText()}
          </span>
        </div>

        {/* Right: Notifications (mobile) + Settings + Logout */}
        <div className="flex items-center gap-1">
          <div className="lg:hidden">
            <NotificationBell variant="inline" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg text-gray-600 dark:text-gray-400"
            onClick={() => navigate("/settings")}
          >
            <Settings size={18} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg text-gray-600 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/30"
            onClick={logout}
          >
            <LogOut size={18} />
          </Button>
        </div>
      </header>

      {/* ─── Mobile Sidebar Drawer (slides in below the header) ─── */}
      {!mobileHost && isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" style={{ top: '3.5rem' }}>
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          {/* Sidebar panel */}
          <div className="relative w-[270px] h-full shadow-xl">
            <Sidebar
              isMobile={true}
              setIsMobileSidebarOpen={setIsMobileSidebarOpen}
              isCollapsed={false}
            />
          </div>
        </div>
      )}

      {/* ─── Desktop Sidebar (never rendered on m.idarah.io) ─── */}
      {!mobileHost && (
      <div className={`hidden lg:block ${isSidebarCollapsed ? 'w-16' : 'w-64'} h-full transition-all duration-300 relative group flex-shrink-0`}>
        <Sidebar
          isMobile={false}
          isCollapsed={isSidebarCollapsed}
        />
        {/* Collapse Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 rounded-full shadow-md border hidden lg:flex"
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </Button>
      </div>
      )}

      {/* ─── Main Content ─── */}
      <main className={cn(
        "flex-1 p-4 md:p-6 w-full transition-all duration-300 bg-white text-black min-h-dvh",
        mobileHost ? "" : "lg:pt-6",
        isMobileSidebarOpen && !mobileHost ? 'overflow-hidden' : 'overflow-y-auto',
        mobileHost ? "" : (isSidebarCollapsed ? 'lg:w-[calc(100%-64px)]' : 'lg:w-[calc(100%-256px)]'),
        // Adjust padding for mobile header hiding
        isAnyModalOpen ? "pt-0" : "pt-[4.5rem]"
      )}>
        {children}
      </main>

      {/* Floating Notification Bell (desktop only; mobile uses the header bell) */}
      <div className="hidden lg:block">
        <NotificationBell />
      </div>
    </div>
  );
};

